import React, { useEffect, useState, Suspense, useRef } from "react";
import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";

import * as actions from "../actions";
import AppHoc from "../../../generics/AppHOC";
import Oidc from "../oidc";
import Saml from "../saml";
import ApiService from "../../../config/api-config";
import Spinner from "../../../common/Spinner";
import OverLappingSpinner from "../../../common/Spinner/OverLappingSpinner";
import css from "./style.less";
import { useI18n } from "../../../i18n/useI18n";
import { IDP_ACTIONS } from "../../../utils/enums";

const ErrorComponent = React.lazy(() =>
  import("../../../common/ErrorComponent")
);

const [
  HbrModal,
  HbrRadioGroup,
  HbrRadio,
  HbrButton,
  HbrIcon
] = reactWrapper([
  "hbr-modal",
  "hbr-radio-group",
  "hbr-radio",
  "hbr-button",
  "hbr-icon"
]);

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

const mapStateToProps = state => {
  return {
    accounts: state.vanalytics.accounts
  };
};
const AccountsModal = props => {
  const { tViews } = useI18n('views');
  const { actions, accounts, setAccounts } = props;
  const { setHideModal, displaySpinner } = actions;
  const { idpAction, idpServerData, idpDefined, error, showSpinner } = accounts;
  const refCloseButton = useRef(null);

  const [idp, setIdp] = useState([
    { value: "oidc", text: tViews('oidcIdp'), checked: false },
    { value: "saml", text: tViews('samlIdp'), checked: true }
  ]);

  const [selectedIdpType, setSelectedIdpType] = useState("saml");

  useEffect(() => {
    if (idpDefined) {
      const loadSmartAccounts = async () => {
        const accounts = await ApiService.getSmartAccountData();
        setAccounts(accounts.data.data);
        displaySpinner({
          showSpinner: false
        });
      };

      loadSmartAccounts();
    }
  }, [displaySpinner, idpDefined, setAccounts]);

  useEffect(() => {
    if (showSpinner && idpServerData.idp_type) {
      displaySpinner({
        showSpinner: false
      });
    }
  }, [displaySpinner, idpServerData.idp_type, showSpinner]);

  const onClose = () => {
    setHideModal();
  };

  const onModalHide = event => {
    if (event.target !== event.currentTarget) {
      return;
    }

    onClose();
  };

  const initialFocus = event => {
    event.preventDefault();
    refCloseButton.current?.element?.setFocus?.({ preventScroll: true });
  };

  const onRadioChange = (event) => {
    const selectedValue = event.target?.value || event.detail?.value || event.value;
    if (selectedValue) {
      setSelectedIdpType(selectedValue);
      const newValue = idp.map((radio) => ({
        ...radio,
        checked: radio.value === selectedValue
      }));
      setIdp(newValue);
    }
  };

  const isDefineIdp = idpAction === IDP_ACTIONS.DEFINE;
  const isLoadingExistingIdp =
    showSpinner && !isDefineIdp && !error && !idpServerData.idp_type;
  const isSamlSelected = isDefineIdp
    ? selectedIdpType === "saml"
    : idpServerData.idp_type === "SAML";
  const isOidcSelected = isDefineIdp
    ? selectedIdpType === "oidc"
    : idpServerData.idp_type === "OIDC";

  return (
    <HbrModal
      size="small"
      open={true}
      noHeader={true}
      className={css["accounts-modal"]}
      onHbr-initial-focus={initialFocus}
      onHbr-hide={onModalHide}
      onHbr-overlay-dismiss={e => e.preventDefault()}
    >
      <div className={css["modal-header"]}>
        <h2>{idpAction}</h2>
        <HbrButton
          ref={refCloseButton}
          variant="text"
          size="small"
          onClick={onClose}
        >
          <HbrIcon name="x" />
        </HbrButton>
      </div>
      {error ? (
        <Suspense fallback={<Spinner />}>
          <ErrorComponent className={"large-dashlet-error"} {...error} />
        </Suspense>
      ) : isLoadingExistingIdp ? (
        <div className={css["modal-spinner-container"]}>
         <OverLappingSpinner/>
        </div>
      ) : (
        <div className={css["form-container"]}>
          {isDefineIdp && (
            <HbrRadioGroup
              name="idp_type"
              onHbr-click={onRadioChange}
              className={css["idp-radio-group"]}
            >
              {idp.map((radio, i) => {
                const { text, ...props } = radio;
                return (
                  <HbrRadio key={i.toString()} {...props}>
                    {text}
                  </HbrRadio>
                );
              })}
            </HbrRadioGroup>
          )}

          {isSamlSelected && <Saml />}
          {isOidcSelected && <Oidc />}
        </div>
      )}
    </HbrModal>
  );
};

AccountsModal.propTypes = {
  actions: PropTypes.object.isRequired,
  accounts: PropTypes.object.isRequired,
  setAccounts: PropTypes.func
};

export default (
  connect(mapStateToProps, mapDispatchToProps)(AppHoc(AccountsModal))
);
